import { authenticatedGet, authenticatedPut } from "../../api/authenticatedFetch";

const BASE = "/api/v1/litellm/monitor";
const AI_COST_BASE = "/api/v1/ai-cost";

// buildMonitorQuery turns a filter object into the query params the monitor
// endpoints accept; only non-empty values are included (absent = today, unfiltered).
export function buildMonitorQuery(filter = {}) {
  const q = {};
  if (filter.source_key) q.source_key = filter.source_key;
  if (filter.model) q.model = filter.model;
  if (filter.call_type) q.call_type = filter.call_type;
  if (filter.hours) q.hours = filter.hours;
  if (filter.granularity === "minute") q.granularity = "minute";
  return q;
}

// The Go backend wraps every payload in a `monitor` envelope (writeJSON(..., "monitor")),
// e.g. { monitor: { totals, freshness, ... } }. Callers read fields directly off the
// returned object, so unwrap the envelope here. Falls back to the raw body if absent.
function unwrap(body) {
  return body && body.monitor !== undefined ? body.monitor : body;
}

// fetchMonitorSummary GET /monitor/summary — today's snapshot (filter-aware).
export async function fetchMonitorSummary(filter = {}) {
  return unwrap(await authenticatedGet(`${BASE}/summary`, buildMonitorQuery(filter)));
}

// fetchMonitorEvents GET /monitor/events — newest-first live tail.
export async function fetchMonitorEvents(filter = {}, limit = 50) {
  return unwrap(await authenticatedGet(`${BASE}/events`, { ...buildMonitorQuery(filter), limit }));
}

// ---------------------------------------------------------------------------
// AI cost re-basis (SP1) — subscription-fee config + the actual/shadow/
// effective-discount summary that proves it end-to-end (see ai_cost_handlers.go).
// ---------------------------------------------------------------------------

// unwrapEnvelope generalizes `unwrap` above for the ai-cost endpoints, which wrap their payload
// under a different key per endpoint (`ai_cost`, `plan`) rather than `monitor`.
function unwrapEnvelope(body, key) {
  return body && body[key] !== undefined ? body[key] : body;
}

// fetchAiCostSummary GET /ai-cost/summary?period=YYYY-MM — the AI-monitor proof tile's data
// (actual re-based cost vs shadow API-equivalent vs effective discount). period is "YYYY-MM";
// omitted defaults server-side to the current month.
export async function fetchAiCostSummary(period) {
  const params = period ? { period } : {};
  return unwrapEnvelope(await authenticatedGet(`${AI_COST_BASE}/summary`, params), "ai_cost");
}

// fetchAiSubscriptionPlan GET /ai-cost/plan — the customer's currently active subscription-fee
// plan (null when none configured — not an error).
export async function fetchAiSubscriptionPlan() {
  return unwrapEnvelope(await authenticatedGet(`${AI_COST_BASE}/plan`, {}), "plan");
}

// putAiSubscriptionPlan PUT /ai-cost/plan — inserts a new versioned plan row. body: provider,
// plan_name, monthly_fee_cents, seats, active_from ("YYYY-MM-DD"), currency (defaults "EUR").
export async function putAiSubscriptionPlan(body) {
  return unwrapEnvelope(await authenticatedPut(`${AI_COST_BASE}/plan`, body), "plan");
}
